import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MxEventName } from '@axon-enterprise/track';
import { MxPropertyName } from '@axon-enterprise/track';
import { MixpanelService } from '@lib/mixpanel';
import { TranslationService } from '@lib/translation';

enum SsoErrorCode {
  AXON_SIGNIN_UNAVAILABLE = '5001',
  AXON_DEFAULT_ERROR = '5000',
  AXON_SIGNED_OUT = '4001',
}

interface ErrorMessage {
  messageKey: string;
  subMessageKey: string;
}

const DEFAULT_ERROR: ErrorMessage = {
  messageKey: 'auth.sso.error.default.message',
  subMessageKey: 'auth.sso.error.default.subMessage',
};

const ERROR_MESSAGES: Record<SsoErrorCode, ErrorMessage> = {
  [SsoErrorCode.AXON_SIGNIN_UNAVAILABLE]: {
    messageKey: 'auth.sso.error.axonSigninUnavailable.message',
    subMessageKey: 'auth.sso.error.axonSigninUnavailable.subMessage',
  },
  [SsoErrorCode.AXON_SIGNED_OUT]: {
    messageKey: 'auth.sso.error.axonSignedOut.message',
    subMessageKey: 'auth.sso.error.axonSignedOut.subMessage',
  },
  [SsoErrorCode.AXON_DEFAULT_ERROR]: DEFAULT_ERROR,
};

@Component({
  selector: 'sn-sso-error',
  templateUrl: './sso-error.view.html',
  styleUrls: ['./sso-error.view.scss'],
})
export class SsoErrorView implements OnInit, OnDestroy {
  public messageKey: string;
  public subMessageKey: string;
  public translationsLoaded = false;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute, 
    private readonly _mixpanelService: MixpanelService,
    private readonly _translationService: TranslationService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {
    const code = route.snapshot.queryParamMap.get('code') as SsoErrorCode;
    const errorMessage = ERROR_MESSAGES[code] || DEFAULT_ERROR;
    this.messageKey = errorMessage.messageKey;
    this.subMessageKey = errorMessage.subMessageKey;

    this._logToMixpanel(route, code);
  }

  public ngOnInit(): void {
    this._translationService.translationReady$().pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.translationsLoaded = true;
      this._changeDetectorRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _logToMixpanel(route: ActivatedRoute, code?: SsoErrorCode): void {
    // only log sso errors, not users being signed out
    if (code === SsoErrorCode.AXON_SIGNED_OUT) return;

    const provider = [
      SsoErrorCode.AXON_DEFAULT_ERROR,
      SsoErrorCode.AXON_SIGNIN_UNAVAILABLE,
    ].includes(code)
      ? 'axon'
      : undefined;

    const message = route.snapshot.queryParamMap.get('message');

    this._mixpanelService.trackMetric(MxEventName.FususLogin, {
      [MxPropertyName.LoginMethod]: 'sso',
      [MxPropertyName.Source]: provider,
      [MxPropertyName.HasError]: true,
      [MxPropertyName.ErrorReason]: message,
    });
  }
}
